import { RouterRootState } from "connected-react-router";
import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import { useFilter } from "../../contexts/table/FilterContext";
import { useTableLegends } from "../../contexts/table/LegendContext";
import { usePagination } from "../../contexts/table/PaginationContext";
import { useSort } from "../../contexts/table/SortContext";
import { useTable } from "../../contexts/table/TableContext";
import { Lookup } from "../../models/Lookup";
import ActivityFrame from "../activity-frame/activity-frame";
import EmptyTableState from "../emptyTableState/EmptyTableState";
import FilterChips from "../filter-chips/filter-chips";
import Paginator from "../paginator/paginator";
import Table from "../table/table";
import TableLegend from "../table/table-legend/table-legend";
import { wrapName } from "../table/table-legend/table-state-wrapper";
import MasterImageProfile from "./live-transactions/master-image-profile";

interface DashboardTableProps {
    onRowClicked?: (rowData: any) => void,
    useTableLegend?: boolean,
    rowPath?: string,
    children?: JSX.Element | JSX.Element[],
    filterClasses?: string,
}

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const DashboardTable: FC<DashboardTableProps> = ({onRowClicked = (data) => { }, useTableLegend = true, rowPath = "", children, filterClasses = "w-full"}) => {
    const tableContext = useTable();
    const sortContext = useSort();
    const filterContext = useFilter();
    const { translate } = useLocale();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const legendContext = useTableLegends();
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));

     const buildCustomCells = () => {
        return tableContext.tableData.map(
            (row) => ({
                ...row,
                name: wrapName(
                    row,
                    {
                        rowPath: {
                            path: rowPath,
                            routeName:
                                routeName,
                        },
                    },
                    legendContext.legendItems,
                    translate.byKey(
                        TranslationKeys.defaultRecord,
                    ),
                ),
                credentialHolder: <MasterImageProfile row={row} />
            }),
        )
    }

    return <ActivityFrame busy={tableContext.busy} className={`w-full ${tableContext.busy ? 'mt-40' : 'h-full overflow-y-auto'}`}>
        <div className="w-full flex mb-3">
            <div className="flex flex-row" style={{ width: "83%" }}>
                <div style={{ width: "10%" }}>
                    <span className="mr-2 text-sm">
                        {translate.byKey(TranslationKeys.filters)}:
                    </span>
                </div>
                <div className={filterClasses}>
                    {filterContext.filters.filter((filter) => filter.showFilterChips !== false).length > 0 ?
                        <FilterChips
                            toggleFilterModal={() => setShowFilterModal(!showFilterModal)}
                            filters={filterContext.filters}
                            disableFilterToggling={false}
                        >
                        </FilterChips>
                        : null}
                </div>
            </div>
            {children}
        </div>

        {
            tableContext.tableData?.length > 0 ?
                <div className="flex rounded overflow-y-hidden mb-3" style={{ maxHeight: "300px", minHeight: "20px" }}>
                    <Table
                        selectRows={false}
                        data={buildCustomCells()}
                        columns={tableContext.tableDescriptor}
                        customise
                        onSort={sortContext.updateDirection}
                        selectedIds={[]}
                        onSelectionUpdated={() => { }}
                        onRowClicked={onRowClicked}
                        tableId={"dashboardTable"}
                    />
                    {useTableLegend ? <TableLegend /> : <></>}
                </div>
                : <EmptyTableState />
        }
    </ActivityFrame>
}

export default DashboardTable;